import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { FormField } from '@/models';
import { makeFormField, validatePassword } from '@/helpers';
import { CustomerError } from '@/services/customer/customer.service';
import { AccountManagementEvent, ErrorEvent } from '@/services/analytics/events';

interface UpdatePasswordForm {
  [key: string]: FormField<string | boolean | null>;
  oldPassword: FormField<string>;
  newPassword: FormField<string>;
}

const UPDATE_PASSWORD_FORM: UpdatePasswordForm = {
  oldPassword: makeFormField(),
  newPassword: makeFormField(),
};

@Component({
  name: 'CmgUpdatePasswordModal',
})
export default class CmgUpdatePasswordModal extends Vue {
  updatePasswordForm = UPDATE_PASSWORD_FORM;
  isLoading = false;
  isFormValid = true;
  formSubmissionErrorMessage = '';

  async mounted() {
    await this.$services.customer.waitForCustomerInitialization();

    this.initializeForm();
  }

  get customerProfile() {
    return this.$appStore.state.customer.profile;
  }

  get form(): UpdatePasswordForm {
    return this.updatePasswordForm;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.updatePasswordModal.visible;
  }

  validateCurrentPassword(): void {
    validatePassword(this.form.oldPassword);
  }

  validateNewPassword(): void {
    validatePassword(this.form.newPassword);
  }

  validateForm() {
    this.isFormValid = true;

    this.validateCurrentPassword();
    this.validateNewPassword();

    for (const key of Object.keys(this.form)) {
      if (!!this.form[key].error) {
        this.form[key].dirty = true;
        this.isFormValid = false;
      }
    }
  }

  initializeForm(): void {
    this.isFormValid = true;
    this.formSubmissionErrorMessage = '';

    this.form.oldPassword = makeFormField();
    this.form.newPassword = makeFormField();
  }

  async onUpdatePassword(): Promise<void> {
    this.isLoading = true;
    this.formSubmissionErrorMessage = '';

    this.validateForm();

    if (!this.isFormValid) {
      this.isLoading = false;
      return;
    }

    try {
      await this.$services.customer.updatePassword(this.form.oldPassword.value, this.form.newPassword.value);

      this.$services.customer.refreshCustomerProfileInfo();
      this.onClose();
    } catch (error) {
      if (error instanceof CustomerError) {
        this.formSubmissionErrorMessage = error.message;
      } else {
        this.formSubmissionErrorMessage = 'Error trying to update customer';
        this.$services.analytics.send(new ErrorEvent('account', this.formSubmissionErrorMessage, 'update-password'));
      }
    }

    this.$services.analytics.send(new AccountManagementEvent('personal-preferences|update-password'));

    this.isLoading = false;
  }

  onClose() {
    this.initializeForm();
    this.$services.ui.setModalVisibility('updatePasswordModal', false);
    this.$services.ui.focusBack();
  }
}
